import axios from 'axios';
import { uploadFile, uploadFileWithInvite } from 'store';
import { base64ToFile, attributeTypes, KeyPurpose } from 'utils';
import { getFreshFileUrl } from 'utils/hooks/getFreshFileUrl';

/**
 * Walk form data and upload any pending signature values.
 *
 * Pending signatures have shape: { _pending: true, type: 'draw'|'type'|'saved'|'upload', dataUrl?, savedRef?, file? }
 * After upload, replaces them with the uploaded StoredFile shape:
 * { ...storedFile, name: 'signature' }.
 *
 * Mutates `data` in-place and returns it.
 *
 * @param {Object} data         - RHF form data (has .attributes and/or .sections[].attributes)
 * @param {string} [inviteToken] - If present, uses the invite-token upload flow
 * @returns {Promise<Object>} The mutated data with uploaded signature values
 */
export const uploadPendingSignatures = async (data, inviteToken) => {
    const processAttributes = async (attributes) => {
        if (!attributes?.length) return;

        for (let i = 0; i < attributes.length; i++) {
            const attr = attributes[i];
            if (attr?.type !== attributeTypes.SIGNATURE) continue;

            const val = attr?.value;
            if (!val?._pending) continue;

            try {
                let storedFile = null;

                if (val.type === 'saved' && (val.savedRef?.key || val.savedRef?.url)) {
                    // Re-upload a FRESH copy of the saved signature (mirrors the
                    // Schedule SignaturePanel flow). Mint a fresh signed GET URL
                    // from the stored key — the .url stamped on userInfo can be
                    // expired by the time the form is submitted. The invite flow
                    // mints via /files/invite/download-url using the invite token.
                    const downloadUrl =
                        (await getFreshFileUrl(val.savedRef, { disposition: 'attachment', inviteToken })) || val.savedRef.url;
                    if (downloadUrl) {
                        const downloadRes = await axios({
                            url: downloadUrl,
                            method: 'GET',
                            responseType: 'blob',
                            useBaseUrl: false,
                        });
                        const blob = new Blob([downloadRes.data]);
                        const file = new File([blob], 'signature.png', { type: 'image/png' });
                        storedFile = inviteToken
                            ? await uploadFileWithInvite(file, KeyPurpose.SIGNATURE, inviteToken)
                            : await uploadFile(file, KeyPurpose.SIGNATURE);
                    }
                } else if ((val.type === 'draw' || val.type === 'type') && val.dataUrl) {
                    const convertedFile = base64ToFile(val.dataUrl, 'signature.png');
                    if (convertedFile) {
                        storedFile = inviteToken
                            ? await uploadFileWithInvite(convertedFile, KeyPurpose.SIGNATURE, inviteToken)
                            : await uploadFile(convertedFile, KeyPurpose.SIGNATURE);
                    }
                } else if (val.type === 'upload' && val.file) {
                    // Uploaded file is already a File — upload as-is, no base64 conversion.
                    storedFile = inviteToken
                        ? await uploadFileWithInvite(val.file, KeyPurpose.SIGNATURE, inviteToken)
                        : await uploadFile(val.file, KeyPurpose.SIGNATURE);
                }

                if (storedFile) {
                    attributes[i].value = { ...storedFile, name: 'signature' };
                }
            } catch (e) {
                // If upload fails, clear the pending value so it doesn't break submit
                attributes[i].value = null;
            }
        }
    };

    // Process top-level attributes
    if (data?.attributes) {
        await processAttributes(data.attributes);
    }

    // Process section-level attributes
    if (data?.sections) {
        for (const section of data.sections) {
            if (section?.attributes) {
                await processAttributes(section.attributes);
            }
        }
    }

    return data;
};
